import React, { useState, useEffect, useCallback } from "react";
import { api, errMsg, ROLE_LABELS } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/common/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Plus, Trash2, Pencil } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ROLES = ["company_admin", "sales_manager", "salesperson", "viewer"];

export default function Users() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState({ name: "", email: "", password: "", role: "salesperson", phone: "" });
  const [delId, setDelId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => { api.get("/users").then(({ data }) => setUsers(data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setF({ name: "", email: "", password: "", role: "salesperson", phone: "" }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setF({ name: u.name, email: u.email, password: "", role: u.role, phone: u.phone || "" }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/users/${editing.id}`, { name: f.name, role: f.role, phone: f.phone });
      else await api.post("/users", f);
      toast.success(`User ${editing ? "updated" : "invited"}`);
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const doDelete = async () => {
    try { await api.delete(`/users/${delId}`); toast.success("User removed"); setDelId(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Users & Team" subtitle="Manage who can access your export workspace">
        <Button onClick={openCreate} className="gap-2 bg-slate-900 hover:bg-slate-800 text-white" data-testid="create-user-button"><Plus className="h-4 w-4" />Invite user</Button>
      </PageHeader>

      <Card className="border border-slate-200/80 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50/80">{["Name", "Email", "Role", "Phone", "Actions"].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider font-semibold text-slate-500">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <Avatar className="h-8 w-8"><AvatarFallback className="bg-slate-900 text-white text-xs">{u.name.split(" ").map((x) => x[0]).slice(0, 2).join("")}</AvatarFallback></Avatar>
                      <span className="font-medium text-slate-800">{u.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                  <TableCell><span className="text-xs font-medium px-2 py-1 rounded-full bg-slate-100 text-slate-700">{ROLE_LABELS[u.role]}</span></TableCell>
                  <TableCell className="text-sm text-slate-500">{u.phone || "—"}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="h-4 w-4 text-slate-500" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-rose-50" onClick={() => setDelId(u.id)} data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="user-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit user" : "Invite user"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label>Full name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-white" data-testid="user-name-input" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={f.email} disabled={!!editing} onChange={(e) => setF({ ...f, email: e.target.value })} className="bg-white" data-testid="user-email-input" /></div>
            {!editing && <div className="space-y-1.5"><Label>Temporary password</Label><Input type="text" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="bg-white" data-testid="user-password-input" /></div>}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Role</Label>
                <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}><SelectTrigger className="bg-white" data-testid="user-role-select"><SelectValue /></SelectTrigger><SelectContent className="bg-white">{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-1.5"><Label>Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="bg-white" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="user-save-button">{saving ? "Saving…" : editing ? "Save" : "Invite"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader><AlertDialogTitle>Remove this user?</AlertDialogTitle><AlertDialogDescription>They will lose access immediately.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="user-delete-confirm">Remove</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
